import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  Component,
  HostListener,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { UsuarioLogado } from '../../../core/models/usuario-login.model';
import { TemaService } from '../../../core/services/tema.service';
import { ControleSessaoService } from '../../../core/services/controle-sessao.service';
import { TituloNavbarService } from '../../../core/services/titulo-navbar.service';
import { NavbarSearchService } from '../../../core/services/navbar-search.service';
import { LucideAngularModule } from 'lucide-angular';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  @Output() tituloAlterado = new EventEmitter<string>();
  @Output() toggleSidebar = new EventEmitter<void>();

  usuario: UsuarioLogado | null = null;
  dropdownAberto = false;
  isDarkMode = false;
  tituloAtual: string = 'Resumo';
  pesquisaNavbarVisivel = false;
  placeholderPesquisaNavbar = 'Pesquisar';
  valorPesquisaNavbar = '';

  subMenuOpen: { [key: string]: boolean } = {
    config: false,
    seguranca: false,
    perfil: false,
    notificacoes: false,
  };

  @ViewChild('dropdownUsuario') dropdownUsuario!: ElementRef;
  @ViewChild('areaUsuario') areaUsuario!: ElementRef;

  private destroy$ = new Subject<void>();

  constructor(
    private controleSessao: ControleSessaoService,
    private router: Router,
    private temaService: TemaService,
    private tituloNavbarService: TituloNavbarService,
    private navbarSearchService: NavbarSearchService,
  ) {}

  ngOnInit(): void {
    // Dados do usuário logado
    const dadosSessao = this.controleSessao.getUsuarioSessao();
    this.usuario = dadosSessao?.usuario || null;

    // 1) Garante que o DOM está em linha com o que está salvo
    this.temaService.sincronizarComDOM();

    // 2) Estado inicial do ícone
    this.isDarkMode = this.temaService.estaEscuro();

    // 3) Mantém o ícone sincronizado com qualquer mudança de tema
    this.temaService.modoEscuro$
      .pipe(takeUntil(this.destroy$))
      .subscribe((modoEscuro) => {
        this.isDarkMode = modoEscuro;
      });

    // Usar o TituloNavbarService (gerenciado pelo layout)
    this.tituloNavbarService.titulo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((titulo) => {
        this.tituloAtual = titulo;
      });

    this.navbarSearchService.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((estado) => {
        this.pesquisaNavbarVisivel = !!estado;
        this.placeholderPesquisaNavbar = estado?.placeholder || 'Pesquisar';
        this.valorPesquisaNavbar = estado?.term || '';
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Botão de alternância de tema
  onToggleDarkMode(): void {
    // Só alterna via serviço (que mexe no DOM + localStorage)
    this.temaService.alternarTema();
    // isDarkMode será atualizado pelo subscribe em modoEscuro$
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  alternarDropdown(): void {
    this.dropdownAberto = !this.dropdownAberto;
  }

  toggleSubMenu(item: string): void {
    this.subMenuOpen[item] = !this.subMenuOpen[item];
  }

  selecionarOpcao(novoTitulo: string): void {
    this.tituloAlterado.emit(novoTitulo);
    this.dropdownAberto = false;
  }

  @HostListener('document:click', ['$event'])
  fecharDropdownFora(evento: Event): void {
    if (
      !this.dropdownUsuario?.nativeElement.contains(evento.target) &&
      !this.areaUsuario?.nativeElement.contains(evento.target)
    ) {
      this.dropdownAberto = false;
    }
  }

  logout(): void {
    // Preserva o tema escolhido pelo usuário
    const temaSalvo = localStorage.getItem('tema');

    // Limpar sessionStorage
    this.controleSessao.logoutSessaoUsuario();

    // Não usar clear() - remover apenas chaves específicas, preservando fixados e tema
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !key.startsWith('modulos_fixados::') && key !== 'tema') {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // Restaura o tema, se existir
    if (temaSalvo) {
      localStorage.setItem('tema', temaSalvo);
    }

    this.router.navigate(['/acesso/login']);
  }

  onNavbarSearchChange(valor: string): void {
    this.navbarSearchService.atualizarTermo(valor);
  }

  limparBuscaNavbar(): void {
    if (!this.valorPesquisaNavbar) return;
    this.valorPesquisaNavbar = '';
    this.navbarSearchService.atualizarTermo('');
  }
}
